import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/supabase.config';
import type { MinisterOption, MinisterSuggestion, NewUserInput, Scope, UserOverview } from '../types';

function fail(error: { message: string } | null): asserts error is null {
  if (error) throw new Error(error.message);
}

/** Calls the admin-users edge function and turns its JSON error into an Error with the message */
async function adminUsers<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-users', { body });
  if (error) {
    let message = 'No se pudo completar la acción';
    if (error instanceof FunctionsHttpError) {
      try {
        const payload = await error.context.json();
        if (payload?.error) message = String(payload.error);
      } catch {
        /* keep the generic message */
      }
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(String(data.error));
  return data as T;
}

export async function getUsersOverview(): Promise<UserOverview[]> {
  const { data, error } = await supabase.rpc('get_users_overview');
  fail(error);
  return (data ?? []) as unknown as UserOverview[];
}

export const createUser = (input: NewUserInput) =>
  adminUsers<{ id: string; username: string; temp_password: string }>({ action: 'create', ...input });

export const resetPassword = (userId: string) =>
  adminUsers<{ temp_password: string }>({ action: 'reset_password', user_id: userId });

export const setUserActive = (userId: string, active: boolean) =>
  adminUsers<{ ok: true }>({ action: 'set_active', user_id: userId, active });

export const renameUser = (userId: string, username: string) =>
  adminUsers<{ ok: true; username: string }>({ action: 'rename', user_id: userId, username });

export async function updateUserBasic(userId: string, fullName: string, contactEmail: string): Promise<void> {
  const { error } = await supabase.rpc('update_user_basic', {
    p_user_id: userId,
    p_full_name: fullName,
    p_contact_email: contactEmail,
  });
  fail(error);
}

export async function linkUserMinister(userId: string, ministerId: string | null): Promise<void> {
  const { error } = await supabase.rpc('link_user_minister', {
    p_user_id: userId,
    p_minister_id: ministerId as string, // null unlinks; the generated type omits it
  });
  fail(error);
}

export async function suggestMinisterUsername(ministerId: string): Promise<MinisterSuggestion> {
  const { data, error } = await supabase.rpc('suggest_minister_username', { p_minister_id: ministerId });
  fail(error);
  const row = data?.[0];
  if (!row) throw new Error('Ministro no encontrado');
  return row;
}

export async function assignUserRole(input: {
  userId: string;
  roleId: string;
  scope: Scope;
  districtId?: string;
  regionId?: string;
}): Promise<void> {
  const { error } = await supabase.rpc('assign_user_role', {
    p_user_id: input.userId,
    p_role_id: input.roleId,
    p_scope: input.scope,
    p_district_id: input.districtId,
    p_region_id: input.regionId,
  });
  fail(error);
}

export async function revokeUserRole(assignmentId: string): Promise<void> {
  const { error } = await supabase.rpc('revoke_user_role', { p_assignment_id: assignmentId });
  fail(error);
}

/** Active ministers matching a name, CI or ADB code */
export async function searchMinisters(query: string): Promise<MinisterOption[]> {
  const q = query.trim().replace(/[%,()]/g, ' ');
  if (q.length < 2) return [];
  const { data, error } = await supabase
    .from('ministers')
    .select('id, adb_code, full_name')
    .eq('is_active', true)
    .or(`full_name.ilike.%${q}%,dni.ilike.%${q}%,adb_code.ilike.%${q}%`)
    .order('full_name')
    .limit(15);
  fail(error);
  return (data ?? []) as MinisterOption[];
}
